import Image from '@/components/atoms/Image';
import {
  VDPUserControlPanel,
  VDPUserControlPanelProps,
} from '@/components/organisms/VDPUserControlPanel/VDPUserControlPanel';
import { ModelLevelDetailTemplateProps } from '@/components/template/ModelLevelDetailTemplate/types';
import { useLockScroll } from '@/hooks/useLockScroll';
import useWindowSize from '@/hooks/useWindowSize';
import { TrimSpecs, TrimStyleWithSpecs } from '@/lib/schema/build/types';
import {
  VehicleType,
  VehicleTypeWithTrimSpecs,
} from '@/lib/schema/inventory/types';
import clsx from 'clsx';
import { FC, useRef, useState } from 'react';
import Accordion from '../Accordion';
import Badge, { BadgeVariants } from '../Badge';
import PopOutModal from '../legacy/PopOutModal/PopOutModal';

export type TrimSpecsModalType = {
  className?: string;
  selectedTrimSpecs: TrimSpecs | null;
  setSelectedTrimSpecs?: (newSelectedTrimSpecs: TrimSpecs) => void;
  trimSpecsOptions?: TrimStyleWithSpecs[];
  open: boolean;
  onClose: () => void;
  product:
    | VehicleType
    | VehicleTypeWithTrimSpecs
    | ModelLevelDetailTemplateProps;
  userControlPanelProps?: VDPUserControlPanelProps;
};

export const TrimSpecsModal: FC<TrimSpecsModalType> = ({
  selectedTrimSpecs,
  setSelectedTrimSpecs,
  trimSpecsOptions = [],
  open,
  onClose,
  className = '',
  product,
  userControlPanelProps,
}) => {
  const { isDesktop } = useWindowSize();
  useLockScroll(open);
  const specsContainerRef = useRef<HTMLDivElement>(null);

  const image = Array.isArray(product.images)
    ? product.images[0]
    : product?.images?.DefaultImages[0];

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <PopOutModal
      open={open}
      onClose={onClose}
      className={`modal-scroll-bar ${className}`}
      titleClassName="whitespace-nowrap"
      title="Full Specs"
      variant="slide-out"
    >
      <div ref={specsContainerRef} className="flex flex-col gap-l pb-3xl">
        <div>
          {'condition' in product && (
            <div className="text-body1Regular text-neutral-900">
              {product.condition}
            </div>
          )}
          <span className="text-h4SemiBold text-neutral-900">
            <span className="text-h4Regular">{product.year}</span>{' '}
            {product.make} {product.model}
            {'trim' in product ? `, ${product.trim}` : ''}
          </span>
        </div>

        {trimSpecsOptions &&
          setSelectedTrimSpecs &&
          trimSpecsOptions.length > 0 && (
            <div className="flex flex-wrap gap-s">
              {trimSpecsOptions.map((trimSpec, i) => {
                return (
                  <div
                    key={`trimSpec-${i}`}
                    className="flex cursor-pointer"
                    onClick={() => setSelectedTrimSpecs(trimSpec.specs)}
                  >
                    <Badge
                      label={trimSpec.trim}
                      variant={
                        trimSpec.specs === selectedTrimSpecs
                          ? BadgeVariants.Blue
                          : BadgeVariants.Gray
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        {image && (
          <Image
            className="object-fit max-h-fit w-full"
            src={image}
            alt={`${product.year} ${product.make} ${product.model}`}
            width={872}
            height={498}
            style={{
              objectFit: isDesktop ? 'cover' : 'contain',
            }}
          />
        )}
        {selectedTrimSpecs?.moreDetails && (
          <div className="rounded-t-small">
            {Object.keys(selectedTrimSpecs?.moreDetails).map(
              (detailCategory, i) => {
                return (
                  <Accordion
                    key={`listCat-${i}`}
                    title={detailCategory}
                    buttonClassName="text-neutral-900 text-body1Regular"
                    className={clsx(
                      'flex-grow border-b border-neutral-200 px-l py-xl transition-colors',
                      {
                        'bg-neutral-100': expandedIndex === i,
                      }
                    )}
                    expanded={expandedIndex === i}
                    setExpanded={(value) => {
                      setExpandedIndex(value ? i : null);
                    }}
                  >
                    {selectedTrimSpecs?.moreDetails[detailCategory].map(
                      (detailValue, j) => (
                        <div
                          key={`listItem-${i}-${j}`}
                          className="flex w-full items-center justify-between border-b border-neutral-200 pb-s pt-xs"
                        >
                          <div
                            className={
                              'basis-1/2 text-body2Light text-neutral-800'
                            }
                          >
                            {detailValue.name}
                          </div>
                          <div className="flex basis-1/2 justify-end text-end text-body2Regular text-neutral-900">
                            {detailValue.value}
                          </div>
                        </div>
                      )
                    )}
                  </Accordion>
                );
              }
            )}
          </div>
        )}
      </div>
      {userControlPanelProps && (
        <div className="hidden m:flex">
          <VDPUserControlPanel
            {...userControlPanelProps}
            handleSendMessage={() => {
              onClose();
              userControlPanelProps.handleSendMessage();
            }}
            handleScrollToCalculator={() => {
              onClose();
              userControlPanelProps.handleScrollToCalculator();
            }}
            isSpecsModal={true}
            setOpenCallModal={() => {
              userControlPanelProps.setOpenCallModal();
            }}
          />
        </div>
      )}
    </PopOutModal>
  );
};
